import React, { useState } from 'react';
import { Upload, FileText, X, Eye, CheckCircle, AlertCircle } from 'lucide-react';

const DocumentsStep = ({ formData, updateFormData, errors, setErrors }) => {
  const [dragActive, setDragActive] = useState({});
  const [previews, setPreviews] = useState({});

  const requiredDocuments = [
    {
      key: 'gstCertificate',
      title: 'GST Certificate',
      description: 'Upload your GST registration certificate (optional)',
      required: false,
      acceptedFormats: '.pdf,.jpg,.jpeg,.png',
      maxSize: '5MB'
    },
    {
      key: 'panCard',
      title: 'PAN Card',
      description: 'Upload PAN card copy (optional)',
      required: false,
      acceptedFormats: '.pdf,.jpg,.jpeg,.png',
      maxSize: '5MB'
    },
    {
      key: 'aadharCard',
      title: 'Aadhar Card',
      description: 'Upload Aadhar card copy (optional)',
      required: false,
      acceptedFormats: '.pdf,.jpg,.jpeg,.png',
      maxSize: '5MB'
    },
    {
      key: 'cancelledCheque',
      title: 'Cancelled Cheque',
      description: 'Upload cancelled cheque or bank statement (optional)',
      required: false,
      acceptedFormats: '.pdf,.jpg,.jpeg,.png',
      maxSize: '5MB'
    },
    {
      key: 'businessLicense',
      title: 'Business License',
      description: 'Upload business license (if applicable)',
      required: false,
      acceptedFormats: '.pdf,.jpg,.jpeg,.png',
      maxSize: '5MB'
    },
    {
      key: 'tradeLicense',
      title: 'Trade License',
      description: 'Upload trade license (if applicable)',
      required: false,
      acceptedFormats: '.pdf,.jpg,.jpeg,.png',
      maxSize: '5MB'
    }
  ];

  const handleFileSelect = (docKey, file) => {
    if (!file) return;

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({
        ...prev,
        [`documents.${docKey}`]: 'File size must be less than 5MB'
      }));
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setErrors(prev => ({
        ...prev,
        [`documents.${docKey}`]: 'Only PDF, JPG, JPEG, and PNG files are allowed'
      }));
      return;
    }

    // Clear any existing errors
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`documents.${docKey}`];
      return newErrors;
    });

    // Update form data
    const newDocuments = {
      ...formData.documents,
      [docKey]: file
    };
    updateFormData({ documents: newDocuments });

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviews(prev => ({
          ...prev,
          [docKey]: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    } else {
      setPreviews(prev => ({
        ...prev,
        [docKey]: null
      }));
    }
  };

  const handleDrag = (e, docKey) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(prev => ({ ...prev, [docKey]: true }));
    } else if (e.type === "dragleave") {
      setDragActive(prev => ({ ...prev, [docKey]: false }));
    }
  };

  const handleDrop = (e, docKey) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(prev => ({ ...prev, [docKey]: false }));

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(docKey, e.dataTransfer.files[0]);
    }
  };

  const removeFile = (docKey) => {
    const newDocuments = {
      ...formData.documents,
      [docKey]: null
    };
    updateFormData({ documents: newDocuments });
    
    setPreviews(prev => {
      const newPreviews = { ...prev };
      delete newPreviews[docKey];
      return newPreviews;
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Document Upload (Optional)</h2>
        <p className="text-gray-600">Upload documents for verification or skip and add them later from your profile</p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Optional Step
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                You can skip document upload for now and complete your registration. Documents can be uploaded later from your seller profile page.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {requiredDocuments.map((doc) => {
          const file = formData.documents[doc.key];
          const hasError = errors[`documents.${doc.key}`];
          const preview = previews[doc.key];
          
          return (
            <div key={doc.key} className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <FileText className="w-5 h-5 mr-2 text-blue-600" />
                    {doc.title}
                    {doc.required && <span className="text-red-500 ml-1">*</span>}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">{doc.description}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Formats: {doc.acceptedFormats} | Max size: {doc.maxSize}
                  </p>
                </div>
              </div>

              {!file ? (
                <div
                  className={`
                    border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                    ${dragActive[doc.key] 
                      ? 'border-blue-400 bg-blue-50' 
                      : hasError 
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }
                  `}
                  onDragEnter={(e) => handleDrag(e, doc.key)}
                  onDragLeave={(e) => handleDrag(e, doc.key)}
                  onDragOver={(e) => handleDrag(e, doc.key)}
                  onDrop={(e) => handleDrop(e, doc.key)}
                  onClick={() => document.getElementById(`file-${doc.key}`).click()}
                >
                  <Upload className={`w-8 h-8 mx-auto mb-2 ${hasError ? 'text-red-400' : 'text-gray-400'}`} />
                  <p className={`text-sm font-medium ${hasError ? 'text-red-600' : 'text-gray-600'}`}>
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {doc.acceptedFormats.replace(/\./g, '').toUpperCase()} up to {doc.maxSize}
                  </p>
                  
                  <input
                    id={`file-${doc.key}`}
                    type="file"
                    className="hidden"
                    accept={doc.acceptedFormats}
                    onChange={(e) => handleFileSelect(doc.key, e.target.files[0])}
                  />
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg p-4 bg-white">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      {preview ? (
                        <img 
                          src={preview} 
                          alt="Preview" 
                          className="w-12 h-12 object-cover rounded border"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-100 rounded border flex items-center justify-center">
                          <FileText className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(file.size)}
                        </p>
                        <div className="flex items-center mt-1">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
                          <span className="text-xs text-green-600">Uploaded</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {preview && (
                        <button
                          type="button"
                          onClick={() => window.open(preview, '_blank')}
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title="Preview"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeFile(doc.key)}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Remove"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {hasError && (
                <div className="mt-2 flex items-center text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {hasError}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Document Guidelines
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Ensure all documents are clear and readable</li>
                <li>Documents should be in original color (not photocopied)</li>
                <li>GST certificate and PAN card details should match the information provided</li>
                <li>Bank account details on cancelled cheque should match the bank information</li>
                <li>All documents will be verified by our team</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <CheckCircle className="h-5 w-5 text-green-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-green-800">
              Security & Privacy
            </h3>
            <div className="mt-2 text-sm text-green-700">
              <p>
                All uploaded documents are encrypted and stored securely. Your documents will only be used for verification purposes and will not be shared with third parties.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentsStep;
